import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { ButtonSimple, DateFilter, TimeFilter } from '../index';

import { actions } from '../../state';
import { isFilterActive } from '../../utils';

import DeviceUptimeChartsRow from './deviceUptimeChartsRow';
import {
  buildDefaultDeviceStatistics,
  buildDeviceStatistics,
  buildPeriodRequestParams,
} from './deviceUptimeCharts.utils';

import sectionStyles from './deviceUptimeCharts.module.scss';
import styles from './deviceUptimeChartsByPeriod.module.scss';

const DeviceUptimeChartsByPeriod = ({
  dispatch,
  availabilityDevicesByPeriod,
}) => {
  const [dateFromFilter, setDateFromFilter] = useState();
  const [dateToFilter, setDateToFilter] = useState();
  const [timeFromFilter, setTimeFromFilter] = useState('00:00');
  const [timeToFilter, setTimeToFilter] = useState('00:00');
  const [sortOrder, setSortOrder] = useState('desc');

  const clearAvailabilityDevicesByPeriod = useCallback(() => {
    dispatch(actions.monitoring.clearAvailabilityDevicesByPeriod());
  }, [dispatch]);

  const fetchHistoricalData = useCallback(() => {
    const params = buildPeriodRequestParams({
      dateFrom: dateFromFilter,
      dateTo: dateToFilter,
      timeFrom: timeFromFilter,
      timeTo: timeToFilter,
    });

    dispatch(actions.monitoring.getAvailabilityDevicesByPeriod(params));
  }, [dateFromFilter, dateToFilter, timeFromFilter, timeToFilter, dispatch]);

  useEffect(() => () => clearAvailabilityDevicesByPeriod(), [
    clearAvailabilityDevicesByPeriod,
  ]);

  useEffect(() => {
    dispatch(actions.monitoring.getAvailabilityDevicesByPeriod({}));
  }, [dispatch]);

  const handleDateFilterSubmit = useCallback(() => {
    if (!dateFromFilter || !dateToFilter) {
      return;
    }

    fetchHistoricalData();
  }, [dateFromFilter, dateToFilter, fetchHistoricalData]);

  const handleTimeFilterSubmit = useCallback(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData]);

  const handleClearFilters = useCallback(() => {
    setDateFromFilter();
    setDateToFilter();
    setTimeFromFilter('00:00');
    setTimeToFilter('00:00');
    clearAvailabilityDevicesByPeriod();
    dispatch(actions.monitoring.getAvailabilityDevicesByPeriod({}));
  }, [clearAvailabilityDevicesByPeriod, dispatch]);

  const hasActiveFilters =
    Boolean(dateFromFilter || dateToFilter) ||
    timeFromFilter !== '00:00' ||
    timeToFilter !== '00:00';

  const deviceStatistics = useMemo(() => {
    const statistics = buildDeviceStatistics(availabilityDevicesByPeriod);

    return statistics || buildDefaultDeviceStatistics();
  }, [availabilityDevicesByPeriod]);

  return (
    <section className={styles.deviceChartsByPeriodSection}>
      <div className={styles.deviceChartsByPeriodHeader}>
        <h2 className={sectionStyles.deviceChartsSectionTitle}>
          Istoriniai duomenys
        </h2>
        <div className={styles.deviceChartsByPeriodFilters}>
          <div className={styles.filterControl}>
            <DateFilter
              title="Pasirinkite laikotarpį"
              name="device_uptime_period"
              lt={dateToFilter}
              setLt={setDateToFilter}
              gt={dateFromFilter}
              setGt={setDateFromFilter}
              onSubmit={handleDateFilterSubmit}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              inLocations
              isActive={isFilterActive('date', dateFromFilter, dateToFilter)}
              disableSorting
            />
          </div>
          <div className={styles.filterControl}>
            <TimeFilter
              title="Pasirinkite laiką"
              name="device_uptime_time"
              lt={timeToFilter}
              setLt={setTimeToFilter}
              gt={timeFromFilter}
              setGt={setTimeFromFilter}
              onSubmit={handleTimeFilterSubmit}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              inLocations
              isActive={isFilterActive('time', timeToFilter, timeFromFilter)}
              disableSorting
            />
          </div>
          {hasActiveFilters && (
            <ButtonSimple type="filter" onClick={handleClearFilters}>
              Išvalyti filtrus
            </ButtonSimple>
          )}
        </div>
      </div>
      <DeviceUptimeChartsRow deviceStatistics={deviceStatistics} />
    </section>
  );
};

const mapStateToProps = state => ({
  availabilityDevicesByPeriod: state.monitoring.availabilityDevicesByPeriod,
});

DeviceUptimeChartsByPeriod.propTypes = {
  dispatch: PropTypes.func.isRequired,
  availabilityDevicesByPeriod: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.shape({
      http_device_count: PropTypes.number,
      http_faulty_device_count: PropTypes.number,
      http_active_device_count: PropTypes.number,
      http_faulty_device_ids: PropTypes.arrayOf(PropTypes.number),
      mqtt_device_count: PropTypes.number,
      mqtt_faulty_device_count: PropTypes.number,
      mqtt_active_device_count: PropTypes.number,
      mqtt_faulty_device_ids: PropTypes.arrayOf(PropTypes.number),
    }),
  ]),
};

export default connect(mapStateToProps)(DeviceUptimeChartsByPeriod);
